import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { CreditCard, CheckCircle, Building2, X, Receipt, AlertTriangle, Send, Mail, Loader2, Share2} from 'lucide-react';
import { toast } from 'sonner';
import { paiementsEnregistres } from './constants';
import { ConfirmerPaiementsBlock } from './ConfirmerPaiementsBlock';

export default function ModalsGroupA({
  // Validation Modal
  showValidationModal, setShowValidationModal, selectedDocument,
  validationData, setValidationData, validating, handleValidatePayment,
  calculateMontantTTC, calculateMontantHT,
  // Assign Modal
  showAssignModal, setShowAssignModal, assignForm, setAssignForm,
  handleAssign, coopers, members, experts,
  // Share Modal
  showShareModal, setShowShareModal, shareForm, setShareForm,
  sharing, handleShare,
  // Partial Payment Modal
  showPartialPaymentModal, setShowPartialPaymentModal,
  partialPaymentData, setPartialPaymentData,
  partialPaymentsHistory, loadingPartialPayments,
  processingPartialPayment, handlePartialPayment,
  downloadPartialReceipt, archiveRecuToGED: archiveRecuToGED,
  openRecuEcheanceModal: openRecuEcheanceModal, generateRecuEcheance: generateRecuEcheance, generatingRecu: generatingRecu,
  // Echeance Modal
  showEcheanceModal, setShowEcheanceModal,
  echeanceForm, setEcheanceForm, updatingEcheance, handleUpdateEcheance,
  // Echeancier Modal
  showEcheancierModal, setShowEcheancierModal,
  echeancierForm, setEcheancierForm, creatingEcheancier,
  handleCreateEcheancier, addEcheanceToForm, removeEcheanceFromForm,
  // Stripe SEPA Modal
  showStripeSepaModal, setShowStripeSepaModal,
  stripeSepaType, setStripeSepaType, stripeProcessing,
  sepaModalEmail, setSepaModalEmail, sepaModalName, setSepaModalName,
  setupStripeSEPAWithEmail,
  // Edit Acompte Modal
  showEditAcompteModal, setShowEditAcompteModal,
  acompteForm, setAcompteForm, saveAcompteModification,
  // Auth (for embedded PaymentLinkSection inside Partial Payment Modal)
  getAuthHeader = () => ({}),
  // Stripe actions (V37.07 — used by CB / SEPA buttons in Partial Payment Modal)
  initiateStripeCBPayment = () => {},
  openStripeSepaModal = () => {}
}) {
  const enregistres = paiementsEnregistres(selectedDocument);
  const confirmerEnregistres = enregistres.length > 0 && !selectedDocument?.paiement_valide && !validationData.saisie_nouveau;
  return (
    <>
      <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                selectedDocument?.type_document === 'avenant' || selectedDocument?.numero_police?.startsWith('AV-')
                  ? 'bg-purple-100'
                  : 'bg-blue-100'
              }`}>
                <CreditCard className={`h-5 w-5 ${
                  selectedDocument?.type_document === 'avenant' || selectedDocument?.numero_police?.startsWith('AV-')
                    ? 'text-purple-600'
                    : 'text-blue-600'
                }`} />
              </div>
              Validation du paiement
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.type_document === 'avenant' || selectedDocument?.numero_police?.startsWith('AV-')
                ? 'Avenant'
                : 'Souscription'
              } : <span className="font-mono text-xs">{selectedDocument?.numero_police}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Résumé */}
            <div className="p-4 rounded-xl bg-gray-50 border">
              <div className="flex items-center gap-3 mb-3">
                <Building2 className="h-5 w-5 text-gray-400" />
                <span className="font-bold text-lg">{selectedDocument?.raison_sociale}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Contribution Solidaire ESS - RCDS BATICO (Montant de base)</p>
                  <p className="font-semibold">
                    {calculateMontantHT(selectedDocument || {})
                      .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Montant Global - Contribution solidaire proposée</p>
                  <p className="font-bold text-lg text-blue-600">
                    {calculateMontantTTC(selectedDocument || {}).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>
            </div>

            {confirmerEnregistres && (
              <ConfirmerPaiementsBlock paiements={enregistres} montantGlobal={calculateMontantTTC(selectedDocument || {})}
                resteServeur={selectedDocument?.reste_a_payer}
                onSaisirNouveau={() => setValidationData({ ...validationData, saisie_nouveau: true })} />
            )}

            {!confirmerEnregistres && (
            <div className="rounded-xl border border-dashed border-amber-400 bg-amber-50 p-3" data-testid="option-a-reclamer">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-amber-900">
                <input type="checkbox" className="h-4 w-4 accent-amber-600" checked={!!validationData.a_reclamer}
                  onChange={(e) => setValidationData({ ...validationData, a_reclamer: e.target.checked,
                    date_limite: validationData.date_limite || new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) })}
                  data-testid="a-reclamer-checkbox" />
                Valider le dossier sans encaissement (montant à réclamer)
              </label>
              {validationData.a_reclamer && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date limite de règlement</Label>
                    <DatePickerFr value={validationData.date_limite} onChange={(v) => setValidationData({ ...validationData, date_limite: v })} className="mt-1 w-full" />
                  </div>
                  <div>
                    <Label>Motif *</Label>
                    <Input value={validationData.motif_reclamer || ''} placeholder="Ex. : virement annoncé, chantier urgent…"
                      onChange={(e) => setValidationData({ ...validationData, motif_reclamer: e.target.value })} className="mt-1" data-testid="a-reclamer-motif" />
                  </div>
                  <p className="col-span-2 text-xs text-amber-800">Le contrat est activé et l&apos;attestation délivrée ; aucun reçu acquitté n&apos;est émis. Le montant apparaît dans l&apos;onglet « À réclamer ».</p>
                </div>
              )}
            </div>
            )}

            {/* Formulaire */}
            {!validationData.a_reclamer && !confirmerEnregistres && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date du virement</Label>
                  <DatePickerFr
                    value={validationData.date_virement}
                    onChange={(v) => setValidationData({...validationData, date_virement: v})}
                    className="mt-1 w-full"
                  />
                </div>
                <div>
                  <Label>Montant reçu (Global)</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={validationData.montant_ttc}
                      onChange={(e) => setValidationData({...validationData, montant_ttc: e.target.value})}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>
              </div>

              <div>
                <Label>Référence du virement</Label>
                <Input
                  placeholder="Ex: VIREMENT-2025-001234"
                  value={validationData.reference_virement}
                  onChange={(e) => setValidationData({...validationData, reference_virement: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Observations (optionnel)</Label>
                <Input
                  placeholder="Notes ou commentaires..."
                  value={validationData.observations}
                  onChange={(e) => setValidationData({...validationData, observations: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            )}

            {/* Alerte montant différent */}
            {!validationData.a_reclamer && !confirmerEnregistres && validationData.montant_ttc && 
             Math.abs(parseFloat(validationData.montant_ttc) - calculateMontantTTC(selectedDocument || {})) > 1 && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Le montant reçu diffère du montant attendu. Vérifiez avant de valider.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleValidatePayment}
              disabled={validating || (confirmerEnregistres ? false : validationData.a_reclamer ? !(validationData.motif_reclamer || '').trim() || !validationData.date_limite : !validationData.montant_ttc)}
              data-testid="validation-paiement-submit"
              className={`${
                selectedDocument?.type_document === 'avenant' || selectedDocument?.numero_police?.startsWith('AV-')
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {validating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validation...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {confirmerEnregistres ? 'Confirmer les paiements enregistrés' : validationData.a_reclamer ? 'Valider — à réclamer' : 'Valider le paiement'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal d'assignation */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-purple-600" />
              Assigner le document
            </DialogTitle>
            <DialogDescription>
              Assigner ce document à un COOPER'S, membre ou expert
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">
                {selectedDocument?.raison_sociale || selectedDocument?.numero_police}
              </p>
              <p className="text-xs text-gray-500">
                {calculateMontantTTC(selectedDocument || {}).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} TTC
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Type d'assignation</Label>
              <Select 
                value={assignForm.assign_type} 
                onValueChange={(v) => setAssignForm({...assignForm, assign_type: v, assign_id: ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cooper">COOPER'S</SelectItem>
                  <SelectItem value="member">Membre</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {assignForm.assign_type === 'cooper' && (
              <div className="space-y-2">
                <Label>Sélectionner un COOPER'S</Label>
                <Select 
                  value={assignForm.assign_id} 
                  onValueChange={(v) => setAssignForm({...assignForm, assign_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un COOPER'S" />
                  </SelectTrigger>
                  <SelectContent>
                    {coopers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.prenom} {c.nom} - {c.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {assignForm.assign_type === 'member' && (
              <div className="space-y-2">
                <Label>Sélectionner un Membre</Label>
                <Select 
                  value={assignForm.assign_id} 
                  onValueChange={(v) => setAssignForm({...assignForm, assign_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un membre" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.prenom} {m.nom} - {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {assignForm.assign_type === 'expert' && (
              <div className="space-y-2">
                <Label>Sélectionner un Expert</Label>
                <Select 
                  value={assignForm.assign_id} 
                  onValueChange={(v) => setAssignForm({...assignForm, assign_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un expert" />
                  </SelectTrigger>
                  <SelectContent>
                    {experts.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.prenom} {e.nom} - {e.specialite || 'Expert'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAssign} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={!assignForm.assign_type || !assignForm.assign_id}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de partage */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-green-600" />
              Partager le paiement validé
            </DialogTitle>
            <DialogDescription>
              Envoyer le reçu de paiement par email
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Info document */}
            <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="h-4 w-4 text-green-600" />
                <p className="text-sm font-medium text-gray-700">
                  {selectedDocument?.raison_sociale || selectedDocument?.numero_police}
                </p>
              </div>
              <p className="text-xs text-gray-500">
                {calculateMontantTTC(selectedDocument || {}).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} TTC - Paiement validé
              </p>
            </div>
            
            {/* Sélection des destinataires */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Destinataires
              </Label>
              
              {/* Email du membre/entreprise */}
              {selectedDocument?.email && (
                <div className="flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50">
                  <Checkbox
                    checked={shareForm.recipients.includes(selectedDocument.email)}
                    onCheckedChange={(checked) => {
                      setShareForm({
                        ...shareForm,
                        recipients: checked
                          ? [...shareForm.recipients, selectedDocument.email]
                          : shareForm.recipients.filter(r => r !== selectedDocument.email)
                      });
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{selectedDocument.raison_sociale}</p>
                    <p className="text-xs text-gray-500">{selectedDocument.email}</p>
                  </div>
                  <Badge variant="outline" className="text-white bg-blue-600 border-blue-700 font-semibold">Entreprise</Badge>
                </div>
              )}
              
              {/* COOPER associé */}
              {selectedDocument?.cooper?.email && (
                <div className="flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50">
                  <Checkbox
                    checked={shareForm.recipients.includes(selectedDocument.cooper.email)}
                    onCheckedChange={(checked) => {
                      setShareForm({
                        ...shareForm,
                        recipients: checked
                          ? [...shareForm.recipients, selectedDocument.cooper.email]
                          : shareForm.recipients.filter(r => r !== selectedDocument.cooper.email)
                      });
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{selectedDocument.cooper.prenom} {selectedDocument.cooper.nom}</p>
                    <p className="text-xs text-gray-500">{selectedDocument.cooper.email}</p>
                  </div>
                  <Badge variant="outline" className="text-white bg-purple-600 border-purple-700 font-semibold">COOPER'S</Badge>
                </div>
              )}
              
              {/* Membre associé */}
              {selectedDocument?.member?.email && (
                <div className="flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50">
                  <Checkbox
                    checked={shareForm.recipients.includes(selectedDocument.member.email)}
                    onCheckedChange={(checked) => {
                      setShareForm({
                        ...shareForm,
                        recipients: checked
                          ? [...shareForm.recipients, selectedDocument.member.email]
                          : shareForm.recipients.filter(r => r !== selectedDocument.member.email)
                      });
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{selectedDocument.member.prenom} {selectedDocument.member.nom}</p>
                    <p className="text-xs text-gray-500">{selectedDocument.member.email}</p>
                  </div>
                  <Badge variant="outline" className="text-white bg-green-600 border-green-700 font-semibold">Membre</Badge>
                </div>
              )}
              
              {/* Email personnalisé */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Ajouter un email personnalisé</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@exemple.com"
                    id="custom-email"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const input = document.getElementById('custom-email');
                      const email = input?.value?.trim();
                      if (email && email.includes('@') && !shareForm.recipients.includes(email)) {
                        setShareForm({
                          ...shareForm,
                          recipients: [...shareForm.recipients, email]
                        });
                        input.value = '';
                        toast.success('Email ajouté');
                      }
                    }}
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
              
              {/* Liste des emails sélectionnés */}
              {shareForm.recipients.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg">
                  {shareForm.recipients.map((email, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {email}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-red-500" 
                        onClick={() => setShareForm({
                          ...shareForm,
                          recipients: shareForm.recipients.filter((_, i) => i !== idx)
                        })}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* Options */}
            <div className="flex items-center gap-3 p-2 border rounded-lg">
              <Checkbox
                checked={shareForm.include_recu}
                onCheckedChange={(checked) => setShareForm({...shareForm, include_recu: checked})}
              />
              <div>
                <p className="text-sm font-medium">Inclure le reçu PDF</p>
                <p className="text-xs text-gray-500">Joindre le reçu de paiement en pièce jointe</p>
              </div>
            </div>
            
            {/* Message */}
            <div className="space-y-2">
              <Label>Message personnalisé</Label>
              <Textarea
                value={shareForm.message}
                onChange={(e) => setShareForm({...shareForm, message: e.target.value})}
                placeholder="Votre message..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleShare} 
              className="bg-green-600 hover:bg-green-700"
              disabled={sharing || shareForm.recipients.length === 0}
            >
              {sharing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer ({shareForm.recipients.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL PAIEMENT PARTIEL */}
    </>
  );
}
